import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './UnitHistory.css';
import { getPropertyUnitTenancyHistory } from '../../../api/services/propertyService';

const UnitHistory = ({ propertyId, unitId, unitLabel: initialUnitLabel = '', onBack }) => {
  const [historyRows, setHistoryRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const loadHistory = useCallback(async () => {
    if (!propertyId || !unitId) {
      setLoading(false);
      setError('Missing property or unit information.');
      setHistoryRows([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await getPropertyUnitTenancyHistory(propertyId, unitId);
      const rows = Array.isArray(response?.data) ? response.data : [];
      setHistoryRows(rows);
    } catch (err) {
      setError(err?.message || 'Failed to load tenancy history.');
      setHistoryRows([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, unitId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const unitLabel = useMemo(() => {
    const knownLabel = String(initialUnitLabel || '').trim();
    if (knownLabel) return knownLabel;
    if (historyRows.length > 0) {
      const row = historyRows[0];
      return row.unitName || row.unitNumber || 'Unknown unit';
    }
    return loading ? 'Loading unit...' : 'Unknown unit';
  }, [historyRows, initialUnitLabel, loading]);

  // Filter history based on search query and filter type
  const filteredHistory = useMemo(() => {
    let filtered = historyRows;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(entry => {
        const fullName = `${entry.userFirstName || ''} ${entry.userLastName || ''}`.trim().toLowerCase();
        return (
          fullName.includes(query) ||
          entry.userEmail?.toLowerCase().includes(query) ||
          entry.tenantCode?.toLowerCase().includes(query) ||
          entry.status?.toLowerCase().includes(query)
        );
      });
    }

    // Apply filter type
    if (filterType === 'active') {
      filtered = filtered.filter(entry => entry.status === 'ACTIVE');
    } else if (filterType === 'notice') {
      filtered = filtered.filter(entry => !!entry.onNotice);
    } else if (filterType === 'ended') {
      filtered = filtered.filter(entry => !!entry.endDate && entry.status !== 'ACTIVE');
    }

    return [...filtered].sort((a, b) => {
      const aTs = new Date(a.createdAt || a.startDate || 0).getTime();
      const bTs = new Date(b.createdAt || b.startDate || 0).getTime();
      return bTs - aTs;
    });
  }, [historyRows, searchQuery, filterType]);

  return (
    <div className="unit-history-container">
      <div className="unit-history-header">
        <div>
          <h1 className="page-title">Unit History</h1>
          <p className="unit-info">
            <strong>Unit:</strong> {unitLabel}
          </p>
        </div>
      </div>

      {/* Search and Filter Controls */}
      {!loading && historyRows.length > 0 && (
        <div className="history-controls">
          <div className="history-search-container">
            <input
              type="text"
              className="history-search-input"
              placeholder="Search by tenant name, email or code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="history-filter-dropdown"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">All Records</option>
            <option value="active">Active</option>
            <option value="notice">On Notice</option>
            <option value="ended">Ended</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="empty-state">
          <p>Loading history...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          <p>{error}</p>
        </div>
      ) : historyRows.length === 0 ? (
        <div className="empty-state">
          <p>No tenancy history found for this unit yet.</p>
        </div>
      ) : filteredHistory.length === 0 ? (
        <div className="empty-state">
          <p>No history entries match your search criteria.</p>
        </div>
      ) : (
        <div className="history-timeline">
          {filteredHistory.map((entry, index) => (
            <div key={entry.id || index} className="history-entry">
              <div className="history-entry-header">
                <div className="history-entry-title">
                  <h3>{`${entry.userFirstName || ''} ${entry.userLastName || ''}`.trim() || 'Unknown tenant'}</h3>
                  {entry.tenantCode && (
                    <span className="tenant-id-badge">{entry.tenantCode}</span>
                  )}
                </div>
                <div className="history-entry-dates">
                  {entry.startDate && (
                    <span className="date-badge">
                      <strong>Start:</strong> {formatDate(entry.startDate)}
                    </span>
                  )}
                  {entry.endDate && (
                    <span className="date-badge">
                      <strong>End:</strong> {formatDate(entry.endDate)}
                    </span>
                  )}
                </div>
              </div>

              <div className="history-details-grid">
                <div className="history-detail-item">
                  <span className="detail-label">Status</span>
                  <span className={`status-badge ${(entry.status || '').toLowerCase()}`}>{entry.status || 'N/A'}</span>
                </div>
                <div className="history-detail-item">
                  <span className="detail-label">On Notice</span>
                  <span className={`notice-pill ${entry.onNotice ? 'notice-yes' : 'notice-no'}`}>
                    {entry.onNotice ? 'Yes' : 'No'}
                  </span>
                </div>
                {entry.userEmail && (
                  <div className="history-detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{entry.userEmail}</span>
                  </div>
                )}
                {entry.noticeStatus && (
                  <div className="history-detail-item">
                    <span className="detail-label">Notice Status</span>
                    <span className="detail-value">{entry.noticeStatus}</span>
                  </div>
                )}
              </div>

              {entry.noticeReason && (
                <div className="history-comments">
                  <strong>Notice Reason</strong>
                  <p>{entry.noticeReason}</p>
                </div>
              )}

              <div className="history-meta">
                <small>Recorded: {formatDate(entry.createdAt)}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UnitHistory;
